import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Plus, Clock, Utensils, ArrowLeft } from 'lucide-react';

interface CalendarEvent {
  id: string;
  type: 'workout' | 'nutrition';
  title: string;
  completed: boolean;
  time?: string;
}

interface CalendarDay {
  date: Date;
  events: CalendarEvent[];
  isToday: boolean;
  isCurrentMonth: boolean;
}

const CalendarSchedulePanel: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const generateCalendarDays = (): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days: CalendarDay[] = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const events: CalendarEvent[] = [];
      
      if (date.getDay() === 1 || date.getDay() === 3 || date.getDay() === 5) {
        events.push({
          id: `workout-${date.getTime()}`,
          type: 'workout',
          title: 'Upper Body Strength',
          completed: date < today,
          time: '9:00 AM'
        });
      }
      
      if (date.getDay() !== 0) {
        events.push({
          id: `nutrition-${date.getTime()}`,
          type: 'nutrition',
          title: 'Meal Planning',
          completed: date < today,
          time: '12:00 PM'
        });
      }
      
      days.push({
        date,
        events,
        isToday: date.toDateString() === today.toDateString(),
        isCurrentMonth: date.getMonth() === month
      });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
  };

  const getEventDots = (events: CalendarEvent[]) => {
    const workoutEvent = events.find(e => e.type === 'workout');
    const nutritionEvent = events.find(e => e.type === 'nutrition');
    
    return (
      <div className="flex justify-center space-x-1 mt-1">
        {workoutEvent && (
          <div 
            className="w-2 h-2 rounded-full"
            style={{
              background: workoutEvent.completed 
                ? 'var(--color-primary)' 
                : 'var(--color-secondary)'
            }}
          />
        )}
        {nutritionEvent && (
          <div 
            className="w-2 h-2 rounded-full"
            style={{
              background: nutritionEvent.completed 
                ? 'var(--color-accent)' 
                : 'rgba(156, 106, 222, 0.3)'
            }}
          />
        )}
      </div>
    );
  };

  const handleBackToDashboard = () => {
    window.dispatchEvent(new CustomEvent('navigate-to-component', {
      detail: { componentId: 'dashboard' }
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToDashboard}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'var(--gradient-card-bg)',
              color: 'var(--color-text-muted)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
            Schedule
          </h2>
        </div>
        <button
          className="p-3 rounded-lg transition-all duration-200"
          style={{
            background: 'var(--gradient-primary)',
            color: 'var(--color-white)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white rounded-xl p-6" style={{ boxShadow: 'var(--shadow-lg)' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 rounded-lg transition-all duration-200"
              style={{
                background: 'var(--gradient-card-bg)',
                color: 'var(--color-text-muted)',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 rounded-lg transition-all duration-200"
              style={{
                background: 'var(--gradient-card-bg)',
                color: 'var(--color-text-muted)',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center py-2">
              <span className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>
                {day}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, index) => (
            <button
              key={index}
              onClick={() => setSelectedDate(day.date)}
              className="aspect-square p-2 rounded-lg transition-all duration-200 relative"
              style={{
                background: day.isToday 
                  ? 'var(--gradient-primary)' 
                  : selectedDate?.toDateString() === day.date.toDateString()
                  ? 'var(--color-secondary)'
                  : 'transparent',
                color: day.isToday 
                  ? 'var(--color-white)' 
                  : !day.isCurrentMonth 
                  ? 'var(--color-text-muted)' 
                  : 'var(--color-text)',
                opacity: day.isCurrentMonth ? 1 : 0.5
              }}
            >
              <span className="text-sm font-medium">
                {day.date.getDate()}
              </span>
              {day.events.length > 0 && getEventDots(day.events)}
            </button>
          ))}
        </div>
      </div>

      {selectedDate && (
        <div className="bg-white rounded-xl p-6" style={{ boxShadow: 'var(--shadow-lg)' }}>
          <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h4>
          
          <div className="space-y-3">
            {calendarDays
              .find(day => day.date.toDateString() === selectedDate.toDateString())
              ?.events.map(event => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-4 rounded-lg"
                  style={{
                    background: event.completed 
                      ? 'rgba(58, 175, 169, 0.1)' 
                      : 'var(--gradient-card-bg)',
                    border: `1px solid ${event.completed ? 'var(--color-primary)' : 'rgba(0,0,0,0.08)'}`
                  }}
                >
                  <div className="flex items-center space-x-3">
                    {event.type === 'workout' ? (
                      <Clock className="h-5 w-5" style={{ color: 'var(--color-primary)' }} />
                    ) : (
                      <Utensils className="h-5 w-5" style={{ color: 'var(--color-accent)' }} />
                    )}
                    <div>
                      <p className="font-medium" style={{ color: 'var(--color-text)' }}>
                        {event.title}
                      </p>
                      {event.time && (
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {event.time}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {event.completed && (
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--color-primary)' }}
                    >
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
              )) || (
                <p className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
                  No events scheduled for this day
                </p>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarSchedulePanel;
